import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import PostCard from '../components/ui/PostCard';
import { users, groups, posts } from '../data/mockData';

const ProfileContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const ProfileAvatar = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: ${({ theme }) => theme.spacing.xl};
  border: 4px solid ${({ theme }) => theme.colors.background};
  box-shadow: ${({ theme }) => theme.shadows.md};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-right: 0;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  display: flex;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: center;
  }
`;

const ProfileUsername = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

const ProfileRank = styled.div`
  display: inline-block;
  background: ${({ theme }) => theme.gradients.primary};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ProfileBio = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  line-height: ${({ theme }) => theme.typography.lineHeights.normal};
`;

const ProfileStats = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: center;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const ProfileActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: center;
  }
`;

const ActionButton = styled.button`
  background: ${({ theme, primary }) => primary ? theme.gradients.primary : 'transparent'};
  color: ${({ theme, primary }) => primary ? 'white' : theme.colors.text.primary};
  border: ${({ theme, primary }) => primary ? 'none' : `1px solid ${theme.colors.border}`};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    background: ${({ theme, primary }) => primary ? theme.gradients.primary : theme.colors.cardBg};
  }
`;

const ProfileContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const ProfileMainContent = styled.div``;

const ProfileSidebar = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    order: -1;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  
  span {
    margin-left: auto;
    font-size: ${({ theme }) => theme.typography.fontSizes.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
    color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Tab = styled.button`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.text.secondary};
  border-bottom: 2px solid ${({ theme, active }) => active ? theme.colors.primary : 'transparent'};
  background: transparent;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const BadgesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Badge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80px;
`;

const BadgeIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ theme, color }) => color || theme.gradients.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const BadgeName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ProgressContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const ProgressTitle = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
`;

const ProgressValue = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ProgressBar = styled.div`
  height: 8px;
  background-color: ${({ theme }) => theme.colors.cardBg};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ percent }) => `${percent}%`};
  background: ${({ theme }) => theme.gradients.primary};
  border-radius: ${({ theme }) => theme.borderRadius.full};
`;

const GroupsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const GroupItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.primary};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.cardBg};
  }
`;

const GroupIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme }) => theme.colors.cardBg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  margin-right: ${({ theme }) => theme.spacing.sm};
`;

const GroupInfo = styled.div`
  flex: 1;
`;

const GroupName = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const GroupMembers = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const FriendsList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FriendItem = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FriendAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const FriendName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  text-align: center;
`;

// Military-style ranks for the gamification system
const RANKS = [
  { name: 'Private', requiredPoints: 0, color: 'linear-gradient(90deg, #B8B8B8 0%, #D6D6D6 100%)' },
  { name: 'Corporal', requiredPoints: 100, color: 'linear-gradient(90deg, #8C8C8C 0%, #A9A9A9 100%)' },
  { name: 'Sergeant', requiredPoints: 250, color: 'linear-gradient(90deg, #5C5C5C 0%, #7A7A7A 100%)' },
  { name: 'Lieutenant', requiredPoints: 500, color: 'linear-gradient(90deg, #FFD700 0%, #FFF176 100%)' },
  { name: 'Captain', requiredPoints: 1000, color: 'linear-gradient(90deg, #FFA000 0%, #FFCA28 100%)' },
  { name: 'Major', requiredPoints: 2000, color: 'linear-gradient(90deg, #FF6F00 0%, #FFA726 100%)' },
  { name: 'Colonel', requiredPoints: 5000, color: 'linear-gradient(90deg, #C62828 0%, #EF5350 100%)' },
  { name: 'General', requiredPoints: 10000, color: 'linear-gradient(90deg, #1A237E 0%, #3949AB 100%)' },
];

// Achievement badges
const BADGES = [
  { name: 'First Post', icon: '📝', color: 'linear-gradient(90deg, #4CAF50 0%, #81C784 100%)' },
  { name: 'Popular Post', icon: '🔥', color: 'linear-gradient(90deg, #FF5722 0%, #FF8A65 100%)' },
  { name: 'Helpful Comment', icon: '💬', color: 'linear-gradient(90deg, #2196F3 0%, #64B5F6 100%)' },
  { name: 'Group Creator', icon: '👑', color: 'linear-gradient(90deg, #FFC107 0%, #FFE082 100%)' },
  { name: 'Daily Login', icon: '📅', color: 'linear-gradient(90deg, #9C27B0 0%, #CE93D8 100%)' },
  { name: 'Photo Master', icon: '📸', color: 'linear-gradient(90deg, #607D8B 0%, #90A4AE 100%)' },
];

const UserProfilePage = () => {
  const { username } = useParams();
  const [activeTab, setActiveTab] = useState('posts');

  // Find the user by username
  const user = users.find(u => u.username === username) || users[0];

  // Calculate total activity points
  const totalPoints = user.activity.postsCount * 10 + user.activity.commentsCount * 2 + user.activity.likesCount;

  // Determine user's rank
  const userRank = RANKS.reduce((prev, curr) => {
    return totalPoints >= curr.requiredPoints ? curr : prev;
  }, RANKS[0]);

  // Find next rank
  const nextRankIndex = RANKS.findIndex(rank => rank.name === userRank.name) + 1;
  const nextRank = nextRankIndex < RANKS.length ? RANKS[nextRankIndex] : null;

  // Calculate progress to next rank
  const progressToNextRank = nextRank
    ? Math.min(100, ((totalPoints - userRank.requiredPoints) / (nextRank.requiredPoints - userRank.requiredPoints)) * 100)
    : 100;

  // Get user's groups
  const userGroups = groups.filter(group => user.groups.includes(group.id));

  // Get user's friends
  const userFriends = users.filter(u => user.friends.includes(u.id));

  // Get user's posts
  const userPosts = posts.filter(post => post.user.id === user.id);

  return (
    <ProfileContainer>
      <ProfileHeader>
        <ProfileAvatar>
          <img src={user.avatar} alt={user.name} />
        </ProfileAvatar>

        <ProfileInfo>
          <ProfileName>
            {user.name}
            <ProfileUsername>@{user.username}</ProfileUsername>
          </ProfileName>

          <ProfileRank>{userRank.name}</ProfileRank>

          <ProfileBio>{user.bio}</ProfileBio>

          <ProfileStats>
            <StatItem>
              <StatValue>{user.activity.postsCount}</StatValue>
              <StatLabel>Posts</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{user.friends.length}</StatValue>
              <StatLabel>Friends</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{user.groups.length}</StatValue>
              <StatLabel>Groups</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{totalPoints}</StatValue>
              <StatLabel>Points</StatLabel>
            </StatItem>
          </ProfileStats>

          <ProfileActions>
            <ActionButton primary>Add Friend</ActionButton>
            <ActionButton>Message</ActionButton>
          </ProfileActions>
        </ProfileInfo>
      </ProfileHeader>

      <ProfileContent>
        <ProfileMainContent>
          <TabsContainer>
            <Tab
              active={activeTab === 'posts'}
              onClick={() => setActiveTab('posts')}
            >
              Posts
            </Tab>
            <Tab
              active={activeTab === 'activity'}
              onClick={() => setActiveTab('activity')}
            >
              Activity
            </Tab>
            <Tab
              active={activeTab === 'about'}
              onClick={() => setActiveTab('about')}
            >
              About
            </Tab>
          </TabsContainer>

          {activeTab === 'posts' && (
            <>
              {userPosts.length > 0 ? (
                userPosts.map(post => (
                  <PostCard key={post.id} post={post} />
                ))
              ) : (
                <Card>
                  <p>No posts yet.</p>
                </Card>
              )}
            </>
          )}

          {activeTab === 'activity' && (
            <Card>
              <SectionTitle>Recent Activity</SectionTitle>
              <p>Liked a post in Travel Enthusiasts • 2 hours ago</p>
              <p>Commented on "Building Responsive Websites with CSS Grid" • 5 hours ago</p>
              <p>Joined Photography group • 1 day ago</p>
              <p>Posted "Street Photography: Capturing Urban Life" • 1 week ago</p>
            </Card>
          )}

          {activeTab === 'about' && (
            <Card>
              <SectionTitle>About {user.name}</SectionTitle>
              <p><strong>Joined:</strong> {new Date(user.joinedDate).toLocaleDateString()}</p>
              <p><strong>Bio:</strong> {user.bio}</p>
              <p><strong>Rank:</strong> {userRank.name}</p>
              <p><strong>Total Points:</strong> {totalPoints}</p>
            </Card>
          )}
        </ProfileMainContent>

        <ProfileSidebar>
          <Card>
            <SectionTitle>Rank & Achievements</SectionTitle>

            <ProgressContainer>
              <ProgressLabel>
                <ProgressTitle>Current Rank: {userRank.name}</ProgressTitle>
                {nextRank && (
                  <ProgressValue>{totalPoints} / {nextRank.requiredPoints}</ProgressValue>
                )}
              </ProgressLabel>
              <ProgressBar>
                <ProgressFill percent={progressToNextRank} />
              </ProgressBar>
              {nextRank && (
                <div style={{ textAlign: 'right', fontSize: '0.8rem', marginTop: '4px' }}>
                  Next: {nextRank.name}
                </div>
              )}
            </ProgressContainer>

            <BadgesContainer>
              {BADGES.map((badge, index) => (
                <Badge key={index}>
                  <BadgeIcon color={badge.color}>{badge.icon}</BadgeIcon>
                  <BadgeName>{badge.name}</BadgeName>
                </Badge>
              ))}
            </BadgesContainer>

            <div style={{ textAlign: 'center', fontSize: '0.9rem', color: '#666' }}>
              Daily login streak: 7 days
            </div>
          </Card>

          <Card>
            <SectionTitle>Groups <span>See All</span></SectionTitle>
            <GroupsList>
              {userGroups.map(group => (
                <GroupItem key={group.id} to={`/group/${group.id}`}>
                  <GroupIcon>{group.icon}</GroupIcon>
                  <GroupInfo>
                    <GroupName>{group.name}</GroupName>
                    <GroupMembers>{group.members.toLocaleString()} members</GroupMembers>
                  </GroupInfo>
                </GroupItem>
              ))}
            </GroupsList>
          </Card>

          <Card>
            <SectionTitle>Friends <span>See All</span></SectionTitle>
            <FriendsList>
              {userFriends.map(friend => (
                <FriendItem key={friend.id} to={`/profile/${friend.username}`}>
                  <FriendAvatar>
                    <img src={friend.avatar} alt={friend.name} />
                  </FriendAvatar>
                  <FriendName>{friend.name}</FriendName>
                </FriendItem>
              ))}
            </FriendsList>
          </Card>
        </ProfileSidebar>
      </ProfileContent>
    </ProfileContainer>
  );
};

export default UserProfilePage;
